/**
 * Phase 11 — Customer Invoicing settings (Shop Profile sub-tab)
 *
 * Lets owners/managers configure:
 *  - Pre-repair authorization terms (snapshotted onto each estimate at signing)
 *  - Post-repair completion terms (snapshotted onto each invoice at signing)
 *  - "Require pre-repair authorization before work starts" toggle
 *
 * All writes go through `useUpdateShopInvoicingSettings` which upserts via the
 * regular client (RLS scoped to shop). The pre-auth toggle is *enforced*
 * server-side at timer-start; this UI only exposes it.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, ShieldCheck, Save } from 'lucide-react';
import {
  useShopInvoicingSettings,
  useUpdateShopInvoicingSettings,
} from '@/hooks/invoicing';

const DEFAULT_PRE = `By signing below I authorize the shop to perform the diagnostic and repair work described in this estimate. I acknowledge the estimated price and understand that additional necessary work will be communicated to me for approval before being performed.`;

const DEFAULT_POST = `By signing below I acknowledge that the work described in this invoice has been completed to my satisfaction and I authorize release of the vehicle.`;

export const InvoicingSettingsTab = () => {
  const { data: settings, isLoading } = useShopInvoicingSettings();
  const update = useUpdateShopInvoicingSettings();

  const [preTerms, setPreTerms] = useState('');
  const [postTerms, setPostTerms] = useState('');
  const [requirePreAuth, setRequirePreAuth] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setPreTerms(settings.pre_repair_terms ?? DEFAULT_PRE);
    setPostTerms(settings.post_repair_terms ?? DEFAULT_POST);
    setRequirePreAuth(!!settings.require_pre_auth_before_work);
    setDirty(false);
  }, [settings]);

  const handleSave = async () => {
    await update.mutateAsync({
      pre_repair_terms: preTerms,
      post_repair_terms: postTerms,
      require_pre_auth_before_work: requirePreAuth,
    });
    setDirty(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pre-auth enforcement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" />
            Authorization enforcement
          </CardTitle>
          <CardDescription>
            When enabled, technicians will be blocked from starting work on a ticket
            until the customer has signed the pre-repair authorization. Managers can
            override on a case-by-case basis (each override is audit-logged).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">
                Require pre-repair authorization before work starts
              </Label>
              <p className="text-xs text-muted-foreground">
                Server-enforced on timer start. Off by default.
              </p>
            </div>
            <Switch
              checked={requirePreAuth}
              onCheckedChange={(v) => {
                setRequirePreAuth(v);
                setDirty(true);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pre-repair terms */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Pre-repair authorization terms
          </CardTitle>
          <CardDescription>
            Shown above the customer signature on every estimate. The exact text is
            snapshotted to the authorization record at signing time — future edits
            will not change historical signatures.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={preTerms}
            onChange={(e) => {
              setPreTerms(e.target.value);
              setDirty(true);
            }}
            rows={6}
            className="resize-y"
            placeholder={DEFAULT_PRE}
          />
        </CardContent>
      </Card>

      {/* Post-repair terms */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Post-repair completion terms
          </CardTitle>
          <CardDescription>
            Shown above the customer signature when work is complete. Snapshotted at
            signing time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={postTerms}
            onChange={(e) => {
              setPostTerms(e.target.value);
              setDirty(true);
            }}
            rows={6}
            className="resize-y"
            placeholder={DEFAULT_POST}
          />
        </CardContent>
      </Card>

      {/* Save bar */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!dirty || update.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {update.isPending ? 'Saving…' : 'Save invoicing settings'}
        </Button>
      </div>
    </div>
  );
};
